/**
 * @fileoverview AR-805 — dedicated "Google Classroom" section of the teacher
 * assessment drawer (PRD G6/§4D). The teacher fills a 1–5 score per student;
 * saving pushes it to the linked Classroom assignment as a DRAFT (never
 * auto-returned — the success toast makes that explicit). Copied from the
 * prototype's classroom block in
 * arcmosaico-prototype/components/Projects/TeacherAssessmentDrawer.tsx
 * (state L73-81, save L573-619, render L1458-1560), adapted Mantine v9→v6 and
 * fetch→GradePort. Precondition failures (PRD §6) map ClassroomPortError.code
 * to specific i18n messages — never a generic error for a known case.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar, Badge, Divider, Grid, Group, Paper, Stack, Text, Title } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'

import { notifications } from 'context'
import { getInitials } from 'helpers/getInitials'
import { GoogleClassroomLogo } from 'components/GoogleClassroomLogo'
import {
  ClassroomAssignment,
  ClassroomErrorCode,
  ClassroomGradesByStudent,
  getClassroomCapabilities,
  isClassroomPortError,
  useClassroomConnection,
  useClassroomGrade,
  useClassroomIntegrationEnabled,
} from 'integrations/classroom'
import { ScorePicker } from './ScorePicker'
import type { DrawerStudent } from '../types'

const GRADE_ERROR_KEYS: Partial<Record<ClassroomErrorCode, string>> = {
  not_linked: 'classroom.gradeErrorNotLinked',
  student_not_linked: 'classroom.gradeErrorStudentNotLinked',
  no_submission: 'classroom.gradeErrorNoSubmission',
  not_assignment_owner: 'classroom.gradeErrorNotAssignmentOwner',
  needs_reconnection: 'classroom.projectClassroomNeedsReconnection',
}

interface ClassroomGradeSectionProps {
  readonly assignments: ClassroomAssignment[]
  readonly students: DrawerStudent[]
}

export function ClassroomGradeSection(props: ClassroomGradeSectionProps) {
  // Guard BEFORE any hook that would fire a request: grade push is phase 4 —
  // no backend yet and out of this delivery (product decision 2026-07-29).
  if (!getClassroomCapabilities().gradePush) return null
  return <ClassroomGradeSectionInner {...props} />
}

function ClassroomGradeSectionInner({ assignments, students }: ClassroomGradeSectionProps) {
  const { t } = useTranslation()
  const { enabled } = useClassroomIntegrationEnabled()
  const { connected } = useClassroomConnection()
  const { pushGrade } = useClassroomGrade()
  const [gradesByStudent, setGradesByStudent] = useState<ClassroomGradesByStudent>({})
  const tr = (key: string) => t(`classroom.${key}`)

  if (!enabled || !connected || assignments.length === 0) return null

  // Optimistic update + rollback on failure, exactly like the prototype.
  const handleGradeChange = async (activityId: string, studentId: string, score: number) => {
    const previous = gradesByStudent
    setGradesByStudent(prev => ({
      ...prev,
      [studentId]: { ...(prev[studentId] || {}), [activityId]: score },
    }))
    try {
      await pushGrade(activityId, studentId, score)
      notifications.show({
        title: tr('gradeSentToGoogleClassroomTitle'),
        message: tr('gradeSentToGoogleClassroomMessage'),
        color: 'green',
        icon: <IconCheck size={18} />,
      })
    } catch (error) {
      setGradesByStudent(previous)
      const errorKey =
        (isClassroomPortError(error) && GRADE_ERROR_KEYS[error.code]) || 'classroom.failedToSaveClassroomGrade'
      notifications.show({ message: t(errorKey), color: 'red' })
    }
  }

  return (
    <>
      <Divider />
      <Stack spacing="md">
        <Group spacing="xs" align="center" noWrap>
          <GoogleClassroomLogo size={18} />
          <Title order={5}>{tr('googleClassroomSectionTitle')}</Title>
        </Group>
        <Text size="sm" color="dimmed">
          {tr('googleClassroomSectionDescription')}
        </Text>
        {assignments.map(assignment => (
          <Paper
            key={assignment.activityId}
            withBorder
            radius="md"
            p="md"
            sx={{ borderLeftColor: 'var(--mantine-color-grape-6)', borderLeftWidth: 4 }}
          >
            <Stack spacing="md">
              <Text weight={600}>{assignment.activityTitle}</Text>
              {students.map(student => {
                const grade = gradesByStudent[student.id]?.[assignment.activityId]
                return (
                  <Paper key={`${assignment.activityId}-${student.id}`} withBorder radius="sm" p="lg">
                    <Grid align="center">
                      <Grid.Col span={12} sm={6} lg="auto">
                        <Group spacing="sm" noWrap>
                          <Avatar src={student.avatarUrl || undefined} radius="xl" size="md">
                            {getInitials(student.fullName)}
                          </Avatar>
                          <Text weight={600} size="md">
                            {student.fullName}
                          </Text>
                        </Group>
                      </Grid.Col>
                      <Grid.Col span={12} sm={6} lg="content">
                        <Stack spacing={4}>
                          <Group position="apart" align="center" noWrap spacing="xs">
                            <Text size="sm" color="dimmed">
                              {tr('googleClassroomGradeLabel')}
                            </Text>
                            {typeof grade === 'number' && (
                              <Badge size="sm" color="green" leftSection={<IconCheck size={12} />}>
                                {tr('gradeSentToGoogleClassroomBadge')}
                              </Badge>
                            )}
                          </Group>
                          <ScorePicker
                            value={grade}
                            onChange={score => void handleGradeChange(assignment.activityId, student.id, score)}
                            position="left"
                          />
                        </Stack>
                      </Grid.Col>
                    </Grid>
                  </Paper>
                )
              })}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </>
  )
}
